import { handleApiError, handleApiResponse } from "@/methods/api";
import ApiVerified from "@/services/apiVerified";
import { ApiResponse, AzureUploadUrl, AzureUploadUrlWithUUID, IdNamePair } from "@/types/global";
import {
  ChapterPdfInput,
  SmartBook,
  SmartBookChapterDesign,
  SmartBookChapterDesignUpdate,
  SmartBookCreate,
  SmartBookImageUploadsInput,
  SmartBookSubjectChapter,
} from "@/types/smartBooks";

export default class SmartBookApi extends ApiVerified {
  async getChapterPdfUploadUrl(contentType: string, fileMd5Checksum: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<AzureUploadUrlWithUUID>(
        `smart-book/get-chapter-pdf-upload-url`,
        { params: { contentType: contentType, fileMd5Checksum: fileMd5Checksum } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSmartBookImageUploadUrls(
    smartBookImageInputs: Array<SmartBookImageUploadsInput>,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<Array<AzureUploadUrl>>(
        `smart-book/get-smart-book-image-upload-urls`,
        smartBookImageInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async submitChapterPdf(chapterPdfInputs: ChapterPdfInput): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `smart-book/submit-chapter-pdf`,
        chapterPdfInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveSmartBook(smartBookCreateInputs: SmartBookCreate): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `smart-book/save-smart-book`,
        smartBookCreateInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateSmartBook(smartBookUpdateInputs: SmartBook): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `smart-book/update-smart-book`,
        smartBookUpdateInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSmartBooksForCourse(courseId: number, onlyActive: boolean): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<SmartBook>>(
        `smart-book/get-smart-books-for-course`,
        { params: { courseId: courseId, onlyActive: onlyActive } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async mapChaptersToSmartBook(
    smartBookId: number,
    subjectChapterIds: Array<number>,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `smart-book/map-chapters-to-smart-book`,
        { SmartBookId: smartBookId, SubjectChapterIds: subjectChapterIds },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateSmartBookChapterMappings(
    smartBookChapterMappings: Array<SmartBookSubjectChapter>,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `smart-book/update-smart-book-chapter-mappings`,
        smartBookChapterMappings,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSmartBookChapterMappings(
    smartBookId: number,
    onlyActive: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<SmartBookSubjectChapter>>(
        `smart-book/get-smart-book-chapter-mappings`,
        { params: { smartBookId: smartBookId, onlyActive: onlyActive } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getUnmappedSmartBookChapters(smartBookId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `smart-book/get-unmapped-smart-book-chapters`,
        { params: { smartBookId: smartBookId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async deleteSmartBookChapterPdf(
    smartBookId: number,
    subjectChapterId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.delete<boolean>(
        `smart-book/delete-smart-book-chapter-pdf`,
        {
          params: { smartBookId: smartBookId, subjectChapterId: subjectChapterId },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSmartBookChapterPdfContent(
    smartBookId: number,
    subjectChapterId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<SmartBookChapterDesign>(
        `smart-book/get-smart-book-chapter-pdf-content`,
        {
          params: { smartBookId: smartBookId, subjectChapterId: subjectChapterId },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateChapterPdfContent(
    chapterPdfContent: SmartBookChapterDesignUpdate,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `smart-book/update-chapter-pdf-content`,
        chapterPdfContent,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // NOTE: the legacy getChapterPdfQuestionsForReview was removed (AR-0.4) — chapter-PDF
  // question review goes through questionApi.getQuestionPdfQuestionsForReview with
  // pdfContentType=chapter (the flow reviewTaggedQuestionsStore drives).
}
